/*  SEOHead — one place to set per-page <title>, description, canonical, Open
    Graph, Twitter card and JSON-LD. Keeps document.head idempotent (no
    duplicate tags) and auto-cleans on unmount so no stale meta leaks between
    routes. Follows the manual-mutation pattern already used across the app
    (we don't ship react-helmet).

    Usage:
      <SEOHead
        title="Portland Travel Guide"
        description="Where to stay, eat and explore in Portland, Oregon."
        canonical="/destinations/portland"
        image="/og/portland.jpg"
        type="article"
        jsonLd={{ "@type": "Article", ... }}       // or an array of blocks
      />                                                                     */
import { useEffect } from "react";
import { SITE } from "../lib/site";

const DEFAULT_IMAGE = "/android-chrome-512x512.png";
const LD_ID = "seohead-jsonld";

function _setMetaByName(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function _setMetaByProp(prop, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function _setLinkRel(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export default function SEOHead({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const origin = SITE.url || window.location.origin;
    const abs = (p) => (!p ? null : p.startsWith("http") ? p : `${origin}${p.startsWith("/") ? "" : "/"}${p}`);

    const fullTitle = title ? (title.includes(SITE.name) ? title : `${title} — ${SITE.name}`) : null;
    if (fullTitle) document.title = fullTitle;

    _setMetaByName("description", description);
    _setMetaByName("robots", noindex ? "noindex,nofollow" : "index,follow");
    _setLinkRel("canonical", abs(canonical) || window.location.href.split("?")[0]);

    _setMetaByProp("og:title", fullTitle);
    _setMetaByProp("og:description", description);
    _setMetaByProp("og:type", type);
    _setMetaByProp("og:url", abs(canonical) || window.location.href.split("?")[0]);
    _setMetaByProp("og:site_name", SITE.name);
    _setMetaByProp("og:image", abs(image) || abs(DEFAULT_IMAGE));

    _setMetaByName("twitter:card", "summary_large_image");
    _setMetaByName("twitter:title", fullTitle);
    _setMetaByName("twitter:description", description);
    _setMetaByName("twitter:image", abs(image) || abs(DEFAULT_IMAGE));

    // JSON-LD: replace the previous SEOHead block (if any) rather than append.
    document.head.querySelectorAll(`script[data-seohead="${LD_ID}"]`).forEach((n) => n.remove());
    if (jsonLd) {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seohead", LD_ID);
      el.textContent = JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);
      document.head.appendChild(el);
    }

    return () => {
      document.head.querySelectorAll(`script[data-seohead="${LD_ID}"]`).forEach((n) => n.remove());
    };
  }, [title, description, canonical, image, type, noindex, JSON.stringify(jsonLd)]);

  return null;
}
